import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { detectLocale, storedLocale, storeLocale, type Locale } from './locale';

interface LocaleContextValue {
  locale: Locale;
  /**
   * True once the user has explicitly picked a language (this page or a
   * previous visit). The distinction matters downstream: only an explicit
   * choice is written to the account — see the contract notes on the verify
   * and password-login requests.
   */
  chosen: boolean;
  setLocale: (locale: Locale) => void;
}

const LocaleContext = createContext<LocaleContextValue | null>(null);

/**
 * One locale for the whole app. Every screen used to call detectLocale() for
 * itself, which was fine while the locale could never change — a selector
 * needs shared, mutable state or half the screens would switch and half would
 * not.
 */
export function LocaleProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<{ locale: Locale; chosen: boolean }>(
    () => {
      const stored = storedLocale();
      return stored
        ? { locale: stored, chosen: true }
        : { locale: detectLocale(), chosen: false };
    },
  );

  // Screen readers pick pronunciation from <html lang>; it must follow every
  // change, not just the initial detection.
  useEffect(() => {
    document.documentElement.lang = state.locale;
  }, [state.locale]);

  const setLocale = useCallback((locale: Locale) => {
    storeLocale(locale);
    setState({ locale, chosen: true });
  }, []);

  const value = useMemo(
    () => ({ locale: state.locale, chosen: state.chosen, setLocale }),
    [state, setLocale],
  );

  return (
    <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>
  );
}

export function useLocale(): LocaleContextValue {
  const value = useContext(LocaleContext);
  if (!value) throw new Error('useLocale requires a LocaleProvider');
  return value;
}
